import type { ExperimentProps } from '@/types';

export type InviteOptimizationVariantId = 'A' | 'B' | 'C';

export const INVITE_LINK_SNACKBAR_AUTO_HIDE_MS = 2500;

type FeatureValue = ExperimentProps | Record<string, unknown> | null | undefined;

/**
 * Reads `variationid` from GrowthBook JSON (also accepts common casing typos).
 */
export function parseInviteStepOptimizationVariationId(value: FeatureValue): string | undefined {
  if (value == null || typeof value !== 'object') return undefined;
  const o = value as Record<string, unknown>;
  const raw = o.variationid ?? o.variationId ?? o.variation_id;
  if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim();
  return undefined;
}

/** Normalizes API / persisted string values to A | B | C. */
export function normalizeInviteOptimizationVariantId(
  raw: unknown,
): InviteOptimizationVariantId | undefined {
  if (raw == null) return undefined;
  if (typeof raw !== 'string') return undefined;
  const u = raw.trim().toUpperCase();
  if (u === 'A' || u === 'B' || u === 'C') return u;
  return undefined;
}

export function resolvedInviteStepOptimizationVariationId(
  fromGrowthBook: FeatureValue,
): InviteOptimizationVariantId | undefined {
  const raw = parseInviteStepOptimizationVariationId(fromGrowthBook);
  if (!raw) return undefined;
  return normalizeInviteOptimizationVariantId(raw);
}

/**
 * Persisted company custom wins when set (resume same variant/step); otherwise GrowthBook.
 */
export function mergedInviteOptimizationVariant(
  fromCustom: unknown,
  fromGrowthBook: FeatureValue,
): InviteOptimizationVariantId | undefined {
  const saved = normalizeInviteOptimizationVariantId(fromCustom);
  if (saved) return saved;
  return resolvedInviteStepOptimizationVariationId(fromGrowthBook);
}

/** Legacy / control invite step (variation A). */
export function isInviteStepOptimizationControl(fromGrowthBook: FeatureValue): boolean {
  return resolvedInviteStepOptimizationVariationId(fromGrowthBook) === 'A';
}

/** Optimized single-step flow (variation B). */
export function isInviteStepOptimizationB(fromGrowthBook: FeatureValue): boolean {
  return resolvedInviteStepOptimizationVariationId(fromGrowthBook) === 'B';
}

/** Two-step invite + dedicated copy-link screen (variation C). */
export function isInviteStepOptimizationC(fromGrowthBook: FeatureValue): boolean {
  return resolvedInviteStepOptimizationVariationId(fromGrowthBook) === 'C';
}

/**
 * @deprecated Prefer explicit isInviteStepOptimizationA/B/C. Kept for older call sites.
 * "B UI" historically meant anything not A; now B and C differ — do not use for layout.
 */
export function isInviteStepOptimizationNotControl(fromGrowthBook: FeatureValue): boolean {
  return resolvedInviteStepOptimizationVariationId(fromGrowthBook) !== 'A';
}

/** GrowthBook feature key for the invite step A/B/C experiment. */
export const INVITE_STEP_OPTIMIZATION_FEATURE_KEY = 'invite-step-optimization';

/**
 * Whether we should call the API to persist `inviteStepOptimizationVariant`.
 * Extracted for unit tests and to keep the sync component simple (Sonar cognitive complexity).
 */
export function shouldPersistInviteOptimizationVariant(
  companyId: string,
  merged: InviteOptimizationVariantId | undefined,
  onServer: InviteOptimizationVariantId | undefined,
  lastSessionPut: InviteOptimizationVariantId | undefined,
): boolean {
  if (!companyId || merged === undefined) {
    return false;
  }
  if (merged === onServer) {
    return false;
  }
  if (lastSessionPut === merged) {
    return false;
  }
  return true;
}
